import { PLANS_TARGET } from '@/domain/assistant/assistant-nav-targets';
import type {
  IAssistantForbiddenDetails,
  IAssistantWritesOffDetails,
  TAssistantErrorBody,
  TAssistantKind,
} from '@/interfaces/assistant.interface';
import type { IGhCalloutCard } from '../gpthelp-host-contract';
import { ofKind } from '../cards/card-kinds';
import type { ICardFormat } from '../cards/card-text';
import { permissionLockCard, planLockCard } from '../cards/lock-cards';
import {
  quotaCard,
  undoWindowCard,
  updateRequiredCard,
  writesOffCard,
} from '../cards/result-cards';
import type { TWritesOff } from '../cards/result-cards';
import { canOpenPage } from '../tools/tool-display';
import type { IGpthelpToolGate } from '../tools/tool-types';
import {
  lockToggleKey,
  permissionLockKey,
  planLockKey,
  PRICE_OPS,
} from './assistant-locks';

/** The Menú page this member can open («Abrir el Menú», «Ver en el menú»), if any. */
export function menuPageKey(gate: IGpthelpToolGate): string | null {
  if (canOpenPage(gate, 'menu')) return 'menu';
  return canOpenPage(gate, 'menu-items') ? 'menu-items' : null;
}

/** The drawer's stock page (Inventario › Administrar stock). */
const STOCK_PAGE = 'inventory-stock';

/**
 * The stock page this member can open («Ver el stock», plan P5-3: CatalogManage
 * on Plus, like the drawer), if any.
 */
export function stockPageKey(gate: IGpthelpToolGate): string | null {
  return canOpenPage(gate, STOCK_PAGE) ? STOCK_PAGE : null;
}

/** The page of each kind's changes: the Menú, or the stock page (plan P5-3). */
const KIND_PAGE_KEYS: Readonly<
  Record<TAssistantKind, (gate: IGpthelpToolGate) => string | null>
> = {
  'menu.changes': menuPageKey,
  'stock.changes': stockPageKey,
};

/**
 * The page where the member makes a change of `kind` by hand, if they can
 * open it: the Menú, or the stock page for the inventory (plan P5-3).
 */
export function kindPageKey(
  kind: TAssistantKind,
  gate: IGpthelpToolGate
): string | null {
  return ofKind(KIND_PAGE_KEYS, kind)(gate);
}

/**
 * The lock title of a 403 `ASSISTANT_FORBIDDEN`: every branch, this
 * branch's inventory (plan P5-3), prices, or other edits (the refusal names
 * only the op, plan F10), in a category op's own words (W13 decision).
 */
export function forbiddenTitleKey(
  details: IAssistantForbiddenDetails | undefined
): string {
  const op = details?.op;
  if (details?.need === 'allBranches')
    return permissionLockKey('allBranches', op);
  if (details?.need === 'stock') return permissionLockKey('stock', op);
  return permissionLockKey(
    op !== undefined && PRICE_OPS.includes(op) ? 'catalog' : 'edit',
    op
  );
}

/**
 * What a 503 `ASSISTANT_WRITES_OFF` says is switched off: the op it names,
 * or every write when it names none.
 */
export function writesOffOf(
  details: IAssistantWritesOffDetails | undefined
): TWritesOff {
  return details?.op === undefined ? 'all' : 'op';
}

/**
 * The card of an Action API refusal the member should read (plan §9.9),
 * with the id of the card it replaces or shows as; null for the others
 * (they fail the action, and the widget says «No se pudo completar»).
 * `kind`: the refused proposal's (a switched-off write points at its page).
 */
export function refusalCard(
  body: TAssistantErrorBody | null,
  id: string,
  gate: IGpthelpToolGate,
  fmt: ICardFormat,
  kind: TAssistantKind = 'menu.changes'
): IGhCalloutCard | null {
  switch (body?.code) {
    case 'ASSISTANT_UNDO_WINDOW':
      return undoWindowCard(id, fmt);
    case 'ASSISTANT_QUOTA':
      return quotaCard(id, fmt);
    case 'ASSISTANT_UPDATE_REQUIRED':
      return updateRequiredCard(id, fmt);
    case 'ASSISTANT_WRITES_OFF':
      return writesOffCard(
        id,
        writesOffOf(body.details),
        kindPageKey(kind, gate),
        fmt,
        kind
      );
    case 'ASSISTANT_FORBIDDEN': {
      // «Sí puedo ocultar…» only when editing is what the role lacks.
      const toggleKey =
        body.details?.need === 'catalogManage'
          ? lockToggleKey(body.details.op, gate)
          : null;
      return permissionLockCard(
        {
          id,
          titleKey: forbiddenTitleKey(body.details),
          canToggle: toggleKey !== null,
          ...(toggleKey ? { toggleKey } : {}),
        },
        fmt
      );
    }
    case 'ASSISTANT_PLAN_REQUIRED':
      return planLockCard(
        {
          id,
          title: fmt.t(planLockKey(body.details?.op)),
          text: '',
          canSeePlans: canOpenPage(gate, PLANS_TARGET),
        },
        fmt
      );
    default:
      return null;
  }
}
